import {useEffect, useId, useRef, useState} from 'react';

type AirportOption = {iata: string; city: string; name?: string; country?: string};

export default function GroupAirportInput({airports, selected, onChange, multiple = false, label, compact = false}: {
    airports: AirportOption[]; selected: string[]; onChange: (codes: string[]) => void;
    multiple?: boolean; label: string; compact?: boolean;
}) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const input = useRef<HTMLInputElement>(null);
    const list = useRef<HTMLUListElement>(null);
    const id = useId();
    const matches = airports.filter(a => `${a.city} ${a.name || ''} ${a.iata} ${a.country || ''}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
    const selection = airports.find(a => a.iata === selected[0]);
    const selectedLabel = selection ? `${selection.city} · ${selection.iata}` : selected[0] || '';
    const close = () => {setOpen(false); setQuery(''); setActive(-1);};
    const choose = (code: string) => {
        onChange(multiple ? Array.from(new Set([...selected, code])).slice(0, 20) : [code]);
        close();
    };
    useEffect(() => {
        if (open && active >= 0) list.current?.children[active]?.scrollIntoView?.({block: 'nearest'});
    }, [active, open]);

    return <div className="relative min-w-0" onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) close();
    }}>
        <label htmlFor={id} className={compact ? 'sr-only' : 'font-semibold'}>{label}</label>
        {multiple && <div className="mt-2 flex flex-wrap gap-2">{selected.map(code => <button key={code} type="button"
            aria-label={`Remove ${code}`} onClick={() => onChange(selected.filter(c => c !== code))}
            className="rounded-full bg-violet-100 px-3 py-2 text-sm text-violet-800">
            {airports.find(a => a.iata === code)?.city || code} · {code} ×
        </button>)}</div>}
        <div className={`relative flex items-center ${compact ? '' : 'mt-3'}`}>
            <input ref={input} id={id} autoComplete="off" role="combobox" aria-autocomplete="list"
                aria-expanded={open} aria-controls={`${id}-list`} aria-activedescendant={open && active >= 0 ? `${id}-option-${active}` : undefined}
                value={open || multiple ? query : selectedLabel}
                onFocus={() => {setOpen(true); setActive(-1);}}
                onClick={() => setOpen(true)}
                onChange={e => {setQuery(e.target.value); setOpen(true); setActive(-1);}}
                onKeyDown={e => {
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault(); setOpen(true);
                        setActive(index => e.key === 'ArrowDown' ? Math.min(index + 1, matches.length - 1) : Math.max(index - 1, 0));
                    } else if (e.key === 'Enter' && open) {
                        e.preventDefault();
                        const match = matches[active < 0 ? 0 : active];
                        if (match) choose(match.iata);
                    } else if (e.key === 'Escape') { e.preventDefault(); close(); }
                }}
                placeholder="Flughafen suchen oder auswählen"
                className={`w-full min-w-0 pr-10 text-gray-800 outline-none focus:ring-2 focus:ring-violet-300 ${compact ? 'rounded-lg bg-transparent py-1 text-sm' : 'rounded-xl border border-violet-100 bg-white p-3'}`}/>
            <button type="button" tabIndex={-1} aria-label={`${label}: Liste ${open ? 'schließen' : 'öffnen'}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => {if (open) close(); else {input.current?.focus(); setOpen(true);}}}
                className="absolute right-1 rounded-lg px-3 py-2 text-violet-600" aria-expanded={open} aria-controls={`${id}-list`}>
                <span aria-hidden="true">{open ? '▴' : '▾'}</span>
            </button>
        </div>
        {open && <ul ref={list} id={`${id}-list`} role="listbox" aria-label={label} aria-multiselectable={multiple || undefined}
            className="absolute left-0 right-0 z-[60] mt-2 max-h-60 overflow-auto rounded-xl border border-violet-100 bg-white py-1 shadow-lg">
            {matches.map((airport, index) => <li key={airport.iata} id={`${id}-option-${index}`} role="option"
                aria-selected={selected.includes(airport.iata)} onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => setActive(index)} onClick={() => choose(airport.iata)}
                className={`cursor-pointer px-4 py-3 text-sm ${active === index ? 'bg-violet-100' : selected.includes(airport.iata) ? 'bg-violet-50' : ''}`}>
                <span className="font-medium">{airport.city} · {airport.iata}</span>
                {selected.includes(airport.iata) && <span aria-hidden="true" className="ml-2 text-violet-600">✓</span>}
                {airport.country && <span className="mt-0.5 block text-xs text-gray-400">{airport.country}</span>}
            </li>)}
            {!matches.length && <li role="presentation" className="p-4 text-sm text-gray-500">{airports.length ? 'Kein Flughafen gefunden.' : 'Keine Flughäfen verfügbar.'}</li>}
        </ul>}
    </div>;
}
